import React, {
  useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import Loader from '../../../components/loader/loader.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import TableCellExtended from '../../../components/table-cell-extended/table-cell-extended.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import { ROUTE_USER } from '../../../constants/system/routes';
import { SETTINGS_USER_VIEW } from '../../../constants/entity/permissions';

const DealStatusChangesModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isLoading,
    dealStatusChangeListData,
    dealName,
    errorMessage,
    onCloseModal,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const commonHeaderTitle: string = 'Deal Status Change History';
  const headerTitle: string = dealName ? `${commonHeaderTitle}: ${dealName}` : commonHeaderTitle;

  const tableDealStatusChangeListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'createdDate',
      title: 'Date',
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        isSmallScreen ? (
          <TableCell isText>
            {dealStatusChangeItem.createdDate}
          </TableCell>
        ) : (
          <TableCellExtended isText>
            {dealStatusChangeItem.createdDate}
          </TableCellExtended>
        )
      ),
    },
    {
      id: 2,
      name: 'statusFrom',
      title: 'Status From',
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        <TableCell>
          {dealStatusChangeItem.dealStatusFromText}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'statusTo',
      title: 'Status To',
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        <TableCell>
          {dealStatusChangeItem.dealStatusToText}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'createdByUser',
      title: 'User',
      isHidden: isSmallScreen,
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        <TableCell isText>
          {
            dealStatusChangeItem.createdByUser && (
              <FilteredLink
                withNewWindow
                link={`${ROUTE_USER}/${dealStatusChangeItem.createdByUser.id}`}
                permission={SETTINGS_USER_VIEW}
              >
                {dealStatusChangeItem.createdByUser.fullName}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
  ], [isSmallScreen]);

  const closeModal: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  return (
    <ModalLayout isOpen={isOpen} title={headerTitle} onCloseModal={closeModal}>
      <Card isContainer withNoPadding isScrollable={isSmallScreen}>
        {
          isLoading && (
            <Loader isInline />
          )
        }
        {
          !isLoading && (
            <Fragment>
              {
                dealStatusChangeListData && !!dealStatusChangeListData.length ? (
                  <Table>
                    <TableHead isSubHeader>
                      <TableRow>
                        {
                          tableDealStatusChangeListFields.map((tableField: TableField): ReactElement => (
                            <VisibleCell key={tableField.id} tableField={tableField}>
                              <TableCell>
                                {tableField.title}
                              </TableCell>
                            </VisibleCell>
                          ))
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody isPlain withZebraRows>
                      {
                        dealStatusChangeListData.map((dealStatusChangeItem: DealStatusChange): ReactElement => (
                          <TableRow key={dealStatusChangeItem.id}>
                            {
                              tableDealStatusChangeListFields.map((tableField: TableField): ReactElement | boolean => (
                                <VisibleCell key={tableField.id} tableField={tableField}>
                                  {!!tableField.component && tableField.component(dealStatusChangeItem)}
                                </VisibleCell>
                              ))
                            }
                          </TableRow>
                        ))
                      }
                    </TableBody>
                  </Table>
                ) : (
                  <NoContentBlock>
                    No deal status change history found
                  </NoContentBlock>
                )
              }
            </Fragment>
          )
        }
      </Card>
      <ModalButtonBlock isFixed>
        <Button onClick={onCloseModal}>
          Close
        </Button>
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default DealStatusChangesModal;
